<!--
  Situação de uma unidade da rede (chamado NS 2026-0082; Full): o histórico de mudanças, o
  registro de uma nova (situação, desde quando e observação) e o link da pasta no Drive. Abre por
  eventBus.emit('modal.rede-situacao.open', { franquiaId }); avisa 'rede.situacao.atualizada'.
-->
<template>
  <Modal v-if="isVisible" :title="titulo" :visible="isVisible" :closable="true" :style="{ width: '560px' }" data-testid="rede-situacao-modal" @update:visible="fechar">
    <p v-if="!historico" class="quieto">{{ t('Carregando…') }}</p>
    <template v-else>
      <p class="atual">
        {{ t('Situação atual') }}: <strong :style="{ color: corDe(historico.situacao) }">{{ t(rotuloDe(historico.situacao)) }}</strong>
        <span v-if="!historico.situacao_registrada" class="quieto">({{ t('deduzida do cadastro; ninguém registrou ainda') }})</span>
      </p>

      <form class="formulario" data-testid="rede-situacao-form" @submit.prevent="registrar">
        <select-list v-model="situacao" elem-id="rede-situacao" data-testid="rede-situacao" :itens-select="opcoes" :auto-select-first="false" :label="t('Nova situação')" />
        <input-date v-model="desde" elem-id="rede-situacao-desde" data-testid="rede-situacao-desde" :label="t('Desde')" />
        <input-text-area v-model="observacao" elem-id="rede-situacao-observacao" data-testid="rede-situacao-observacao" :label="t('Observação (ex.: distrato, novo franqueado)')" :rows="2" />
        <div class="botoes">
          <app-button type="submit" :label="t('Registrar')" icon="pi pi-check" :loading="enviando" :disabled="!situacao || !desde" data-testid="rede-situacao-registrar" />
        </div>
      </form>

      <form class="formulario pasta" data-testid="rede-pasta-form" @submit.prevent="gravar">
        <input-text v-model="pasta" elem-id="rede-pasta" data-testid="rede-pasta" :label="t('Pasta no Drive')" placeholder="https://drive.google.com/drive/folders/…" />
        <div class="botoes">
          <app-button type="submit" :label="t('Gravar link')" icon="pi pi-save" size="small" :loading="gravando" :disabled="pasta.trim() === (historico.pasta_drive_url ?? '')" data-testid="rede-pasta-gravar" />
          <a v-if="historico.pasta_drive_url" :href="historico.pasta_drive_url" target="_blank" rel="noopener noreferrer" data-testid="rede-pasta-abrir">{{ t('Abrir pasta') }}</a>
        </div>
      </form>

      <h4 class="titulo-historico">{{ t('Histórico') }}</h4>
      <p v-if="historico.historico.length === 0" class="quieto">{{ t('Sem registros.') }}</p>
      <ul v-else class="historico" data-testid="rede-situacao-historico">
        <li v-for="h in historico.historico" :key="h.id">
          <span class="chip" :style="{ color: corDe(h.situacao) }">{{ t(rotuloDe(h.situacao)) }}</span>
          <span>{{ t('desde') }} {{ data(h.desde) }}</span>
          <span v-if="h.observacao" class="quieto">— {{ h.observacao }}</span>
          <span class="quieto">({{ h.autor }})</span>
        </li>
      </ul>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputDate from '../../inputs/InputDate.vue';
import InputText from '../../inputs/InputText.vue';
import InputTextArea from '../../inputs/InputTextArea.vue';
import SelectList from '../../inputs/SelectList.vue';
import { useRede, mensagemDaRede, SITUACOES } from '../../composables/useRede';
import { eventBus } from '../../eventBus';

const { t } = useI18n();
const { historico, carregarHistorico, mudarSituacao, gravarPasta } = useRede();

const isVisible = ref(false);
const franquiaId = ref(0);
const situacao = ref('');
const desde = ref('');
const observacao = ref('');
const enviando = ref(false);
const pasta = ref('');
const gravando = ref(false);

const titulo = computed(() => historico.value?.nome ?? t('Unidade'));
const opcoes = computed(() => SITUACOES.map((s) => ({ ID: s.chave, Name: t(s.rotulo) })));
const rotuloDe = (chave: string): string => SITUACOES.find((s) => s.chave === chave)?.rotulo ?? chave;
const corDe = (chave: string): string => SITUACOES.find((s) => s.chave === chave)?.cor ?? '#1f2328';
/** AAAA-MM-DD → DD/MM/AAAA, sem passar por Date (data sem hora não sofre fuso). */
const data = (iso: string): string => iso.slice(0, 10).split('-').reverse().join('/');

const abrir = (dados?: { franquiaId: number }): void => {
  if (!dados?.franquiaId) return;
  franquiaId.value = dados.franquiaId;
  historico.value = null;
  situacao.value = '';
  desde.value = '';
  observacao.value = '';
  isVisible.value = true;
  pasta.value = '';
  carregarHistorico(dados.franquiaId)
    .then(() => {
      pasta.value = historico.value?.pasta_drive_url ?? '';
    })
    .catch((e: unknown) => eventBus.emit('globalMessage.error', t(mensagemDaRede(e))));
};

const fechar = (): void => {
  isVisible.value = false;
};

const registrar = async (): Promise<void> => {
  enviando.value = true;
  try {
    await mudarSituacao({ franquia_id: franquiaId.value, situacao: situacao.value, desde: desde.value, ...(observacao.value.trim() ? { observacao: observacao.value.trim() } : {}) });
    eventBus.emit('globalMessage.success', t('Situação registrada'));
    eventBus.emit('rede.situacao.atualizada');
    situacao.value = '';
    observacao.value = '';
  } catch (e) {
    eventBus.emit('globalMessage.error', t(mensagemDaRede(e)));
  } finally {
    enviando.value = false;
  }
};

const gravar = async (): Promise<void> => {
  gravando.value = true;
  try {
    await gravarPasta({ franquia_id: franquiaId.value, url: pasta.value.trim() });
    pasta.value = historico.value?.pasta_drive_url ?? '';
    eventBus.emit('globalMessage.success', t(pasta.value ? 'Link da pasta gravado' : 'Link da pasta apagado'));
    eventBus.emit('rede.situacao.atualizada');
  } catch (e) {
    eventBus.emit('globalMessage.error', t(mensagemDaRede(e)));
  } finally {
    gravando.value = false;
  }
};

eventBus.on('modal.rede-situacao.open', abrir);
onBeforeUnmount(() => {
  eventBus.off('modal.rede-situacao.open', abrir);
});
</script>

<style scoped>
.atual { margin: 0 0 12px; }
.formulario { display: flex; flex-direction: column; gap: 10px; padding: 12px; border-radius: 8px; background: #f7f6f2; }
.formulario.pasta { margin-top: 10px; }
.botoes { display: flex; gap: 8px; align-items: center; }
.titulo-historico { margin: 16px 0 6px; font-size: 15px; }
.historico { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; font-size: 14px; }
.historico li { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.chip { font-weight: 700; }
.quieto { color: #5b6168; font-size: 14px; }
</style>
